/**
 * Papers, and what is still missing.
 *
 * Completeness is help, not law. The word "complete" appears nowhere in the
 * Act, so the sentence saying that completeness has no effect on the statutory
 * period is shown every single time this block appears, and never behind a
 * disclosure.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { bi, type Bilingual } from "@ksidc/ontology";
import { useLang, useSession } from "@ksidc/client";
import type { CompletenessResponse, DocumentsResponse } from "../api-types.js";
import { formatDate } from "../format.js";
import { Callout, ErrorNote, Loading, Panel, ProvenanceRow } from "./ui.js";

const KIND_FALLBACK = bi("Something else", "മറ്റെന്തെങ്കിലും");

function fileSize(bytes: number, lang: string): string {
  if (bytes < 1024) return lang === "ml" ? `${bytes} ബൈറ്റ്` : `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const EXTRACTION_STATUS: Record<string, Bilingual> = {
  PENDING: bi("Not yet read", "ഇതുവരെ വായിച്ചിട്ടില്ല"),
  RUNNING: bi("Being read", "വായിച്ചുകൊണ്ടിരിക്കുന്നു"),
  DONE: bi("Read by a model, and not checked by anyone yet", "ഒരു മോഡൽ വായിച്ചു; ആരും പരിശോധിച്ചിട്ടില്ല"),
  FAILED: bi("Could not be read", "വായിക്കാൻ കഴിഞ്ഞില്ല"),
};

export function Documents({ applicationId }: { applicationId: string }): JSX.Element {
  const { api, auth } = useSession();
  const { lang, t } = useLang();

  const [docs, setDocs] = useState<DocumentsResponse | null>(null);
  const [completeness, setCompleteness] = useState<CompletenessResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("TITLE_DEED");
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([
        api.get<DocumentsResponse>(`/applications/${applicationId}/documents`),
        api.get<CompletenessResponse>(`/applications/${applicationId}/completeness`),
      ]);
      setDocs(d);
      setCompleteness(c);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [api, applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const upload = async (): Promise<void> => {
    const chosen = fileRef.current?.files?.[0];
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      // Multipart has to go through fetch directly: the browser sets the
      // boundary on the content type and the shared JSON client cannot.
      const body = new FormData();
      body.append("file", chosen);
      body.append("kind", kind);
      const token = auth ? await auth.token() : null;
      const res = await fetch(`/api/applications/${applicationId}/documents`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text.slice(0, 300));
      }
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const kindLabel = (k: string): Bilingual =>
    completeness?.requiredDocuments.find((d) => d.kind === k)?.label ?? KIND_FALLBACK;

  return (
    <Panel title={bi("Your papers", "നിങ്ങളുടെ രേഖകൾ")}>
      {error ? <ErrorNote error={error} /> : null}

      {completeness === null ? (
        <Loading what={bi("Reading your papers", "നിങ്ങളുടെ രേഖകൾ വായിക്കുന്നു")} />
      ) : (
        <>
          <p>
            {t(
              bi(
                `${completeness.completeness.present} of ${completeness.completeness.total} of the things usually asked for are here.`,
                `സാധാരണ ആവശ്യപ്പെടുന്ന ${completeness.completeness.total} കാര്യങ്ങളിൽ ${completeness.completeness.present} എണ്ണം ഇവിടെയുണ്ട്.`,
              ),
            )}
          </p>

          {/* The one thing a reader must not take from this block. */}
          <Callout
            tone="calm"
            title={bi(
              "This does not change your dates",
              "ഇത് നിങ്ങളുടെ തീയതികളെ മാറ്റുന്നില്ല",
            )}
            provenance={completeness.completeness.provenance}
          >
            <p style={{ marginBottom: 0 }}>{t(completeness.completeness.statutoryEffect)}</p>
          </Callout>

          {completeness.completeness.gaps.length > 0 ? (
            <>
              <h3 style={{ marginTop: "1.2rem" }}>
                {t(bi("Still missing", "ഇനിയും ചേർക്കാനുള്ളത്"))}
              </h3>
              <ul className="items">
                {completeness.completeness.gaps.map((gap) => (
                  <li className="item" key={`${gap.kind}-${gap.key}`}>
                    <div className="item-title">{t(gap.label)}</div>
                    <div className="item-sub">
                      {gap.kind === "DOCUMENT"
                        ? t(bi("A paper to upload", "അപ്‌ലോഡ് ചെയ്യേണ്ട ഒരു രേഖ"))
                        : t(bi("An answer missing from your form", "ഫോറത്തിൽ നൽകാത്ത ഒരു ഉത്തരം"))}
                    </div>
                    <ProvenanceRow items={[gap.provenance]} />
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      )}

      <hr className="rule" />

      <h3>{t(bi("Add a paper", "ഒരു രേഖ ചേർക്കുക"))}</h3>
      <div className="field no-print">
        <label className="field-label" htmlFor="doc-kind">
          {t(bi("What is it?", "ഇത് എന്താണ്?"))}
        </label>
        <select id="doc-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
          {(completeness?.requiredDocuments ?? []).map((d) => (
            <option key={d.kind} value={d.kind}>
              {t(d.label)}
            </option>
          ))}
          <option value="OTHER">{t(KIND_FALLBACK)}</option>
        </select>
      </div>

      <div className="field no-print">
        <label className="field-label" htmlFor="doc-file">
          {t(bi("Choose a file from your device", "ഫയൽ തിരഞ്ഞെടുക്കുക"))}
        </label>
        <input id="doc-file" type="file" ref={fileRef} />
        <p className="field-help">
          {t(bi("Up to 25 MB per file.", "ഓരോ ഫയലിനും 25 MB വരെ."))}
        </p>
      </div>

      <button type="button" className="btn no-print" onClick={() => void upload()} disabled={busy}>
        {busy ? (
          <>
            <span className="spinner" aria-hidden="true" />
            {t(bi("Sending", "അയയ്ക്കുന്നു"))}
          </>
        ) : (
          t(bi("Upload", "അപ്‌ലോഡ് ചെയ്യുക"))
        )}
      </button>

      {docs && docs.documents.length > 0 ? (
        <>
          <hr className="rule" />
          <h3>{t(bi("What you have sent", "നിങ്ങൾ അയച്ചവ"))}</h3>
          <ul className="items">
            {docs.documents.map((d) => (
              <li className="item" key={d.id}>
                <div className="item-title">{t(kindLabel(d.kind))}</div>
                <div className="item-sub">
                  {d.filename} · {fileSize(d.sizeBytes, lang)} ·{" "}
                  {t(
                    bi(
                      `sent on ${formatDate(d.uploadedAt, "en")}`,
                      `${formatDate(d.uploadedAt, "ml")}-ന് അയച്ചു`,
                    ),
                  )}
                </div>
                <div className="item-sub">
                  {t(EXTRACTION_STATUS[d.extraction.status] ?? bi(d.extraction.status, d.extraction.status))}
                </div>
                {d.extraction.status === "DONE" ? (
                  <ProvenanceRow items={[d.extraction.provenance]} />
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Panel>
  );
}
